/**
 * Convert a string to a URL-friendly slug
 *
 * @param text The text to convert to slug
 * @returns URL-friendly slug
 */
export function slugify(text: string): string {
  return (
    text
      .toString()
      .toLowerCase()
      .trim()
      // Replace spaces with hyphens
      .replace(/\s+/g, "-")
      // Remove special characters except hyphens
      .replace(/[^\w\-]+/g, "")
      // Replace multiple hyphens with single hyphen
      .replace(/\-\-+/g, "-")
      // Remove leading/trailing hyphens
      .replace(/^-+/, "")
      .replace(/-+$/, "")
  );
}

/**
 * Convert Indonesian text to slug with proper character mapping
 *
 * @param text The Indonesian text to convert
 * @returns URL-friendly slug
 */
export function slugifyIndonesian(text: string): string {
  // Map Indonesian characters to ASCII equivalents
  const charMap: { [key: string]: string } = {
    á: "a",
    à: "a",
    ä: "a",
    â: "a",
    ā: "a",
    ã: "a",
    é: "e",
    è: "e",
    ë: "e",
    ê: "e",
    ē: "e",
    í: "i",
    ì: "i",
    ï: "i",
    î: "i",
    ī: "i",
    ó: "o",
    ò: "o",
    ö: "o",
    ô: "o",
    ō: "o",
    õ: "o",
    ú: "u",
    ù: "u",
    ü: "u",
    û: "u",
    ū: "u",
    ñ: "n",
    ç: "c",
  };

  // Replace special characters
  let result = text.toLowerCase();
  for (const [char, replacement] of Object.entries(charMap)) {
    result = result.replace(new RegExp(char, "g"), replacement);
  }

  return slugify(result);
}

/**
 * Check if a string is already a valid slug
 *
 * @param text The text to check
 * @returns True if the text is a valid slug
 */
export function isValidSlug(text: string): boolean {
  const slugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
  return slugPattern.test(text);
}

/**
 * Convert user input to slug (handles both Indonesian and English)
 * This is the main function to use for converting user input
 *
 * @param input User input text
 * @returns URL-friendly slug
 */
export function toSlug(input: string): string {
  if (!input || typeof input !== "string") {
    return "";
  }

  // If it's already a valid slug, return as is
  if (isValidSlug(input)) {
    return input;
  }

  // Check if input contains Indonesian characters
  const hasIndonesianChars = /[áàäâāãéèëêēíìïîīóòöôōõúùüûūñç]/i.test(input);

  if (hasIndonesianChars) {
    return slugifyIndonesian(input);
  }

  return slugify(input);
}
